import { useEffect, useState } from 'react';
import { ReportDocument, type FrozenSellerReport, type ReportImages } from '../components/seller-report/ReportDocument';
import { publicReportOptions } from '../services/publicSellerReport';

export function sharedSellerReportMetadata(report: FrozenSellerReport, token: string, origin: string) {
  const reportTitle = report.terminology.reportTitle;
  const reference = report.property.listingReference?.replace(/[\u0000-\u001f\u007f<>]/gu, ' ').replace(/\s+/gu, ' ').trim() || 'Property report';
  const address = report.property.address?.replace(/[\u0000-\u001f\u007f<>]/gu, ' ').replace(/\s+/gu, ' ').trim() || reference;
  const url = `${origin}/seller-report/${encodeURIComponent(token)}`;
  return {
    title: `${reportTitle} | ${reference}`,
    description: `${address} · ${reference} · View your latest secure ${reportTitle}.`.slice(0, 240),
    url,
    imageUrl: `${url}/og.png`,
  };
}

function setMeta(attribute: 'name' | 'property', name: string, content: string) {
  let tag = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.append(tag);
  }
  tag.content = content;
}

export default function SharedSellerReport() {
  const [data, setData] = useState<{ report: FrozenSellerReport; assetUrls: ReportImages } | null>(null);
  const [failed, setFailed] = useState(false);
  const token = window.location.pathname.split('/')[2] ?? '';
  useEffect(() => {
    document.title = 'Keller Williams Report';
    if (new URLSearchParams(window.location.search).get('render') === '1') window.history.replaceState(null, '', window.location.pathname);
    const controller = new AbortController();
    if (!/^[A-Za-z0-9_-]{43}$/.test(token)) { setFailed(true); return; }
    fetch(`/api/seller-report/${encodeURIComponent(token)}`, publicReportOptions({ signal: controller.signal })).then(async (response) => {
      if (!response.ok) throw new Error('Unavailable'); return response.json();
    }).then((result) => {
      const metadata = sharedSellerReportMetadata(result.report, token, window.location.origin);
      document.title = metadata.title;
      setMeta('name', 'description', metadata.description);
      setMeta('property', 'og:title', metadata.title);
      setMeta('property', 'og:description', metadata.description);
      setMeta('property', 'og:type', 'website');
      setMeta('property', 'og:url', metadata.url);
      setMeta('property', 'og:image', metadata.imageUrl);
      setMeta('name', 'twitter:card', 'summary_large_image');
      setMeta('name', 'twitter:title', metadata.title);
      setMeta('name', 'twitter:description', metadata.description);
      setMeta('name', 'twitter:image', metadata.imageUrl);
      setData(result);
    }).catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => controller.abort();
  }, [token]);
  const download = `/seller-report/${encodeURIComponent(token)}/download`;
  return <main className="min-h-screen border-t-4 border-red-800 bg-slate-50 px-4 py-7 sm:px-6 sm:py-10"><div className="mx-auto max-w-[70rem] border border-slate-200 bg-white p-5 sm:p-8 lg:p-10">{failed ? <section role="alert" className="py-16 text-center"><p className="text-sm font-semibold text-red-800">Keller Williams</p><h1 className="mt-3 text-xl font-semibold text-slate-900">Report unavailable</h1><p className="mt-3 text-sm text-slate-500">This report link is invalid, expired, or no longer available.</p></section> : data ? <ReportDocument report={data.report} images={data.assetUrls} download={download} onPrint={() => window.open(download, '_blank', 'noopener,noreferrer')} /> : <p role="status" className="py-16 text-center text-sm text-slate-500">Loading seller report…</p>}</div></main>;
}